import { useEffect } from "react";

const FOOTER_SELECTOR = ".ip-fixed-form-footer, .ia-form-footer";
const GAP_PX = 12;
const DEFAULT_BOTTOM_PX = 24;

/**
 * Lifts the AI float / recorder when a fixed form footer (Submit bar)
 * occupies the bottom of the viewport.
 */
const useAiFloatClearance = (enabled = true) => {
  useEffect(() => {
    if (!enabled) {
      document.documentElement.style.removeProperty("--ai-fab-clearance");
      return undefined;
    }

    let raf = 0;

    const apply = () => {
      cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(() => {
        const viewportH = window.innerHeight;
        let clearance = 0;

        document.querySelectorAll(FOOTER_SELECTOR).forEach((el) => {
          const style = window.getComputedStyle(el);
          if (
            style.display === "none" ||
            style.visibility === "hidden" ||
            style.opacity === "0"
          ) {
            return;
          }

          const rect = el.getBoundingClientRect();
          if (rect.height < 8) return;
          // Only care about bars parked on the viewport bottom
          if (rect.bottom < viewportH - 8) return;
          if (rect.top >= viewportH) return;

          const needed = Math.ceil(viewportH - rect.top + GAP_PX);
          clearance = Math.max(clearance, needed);
        });

        const value =
          clearance > DEFAULT_BOTTOM_PX ? `${clearance}px` : "0px";
        document.documentElement.style.setProperty("--ai-fab-clearance", value);
      });
    };

    apply();
    window.addEventListener("resize", apply);
    window.addEventListener("scroll", apply, true);

    const mo = new MutationObserver(apply);
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class", "hidden"],
    });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", apply);
      window.removeEventListener("scroll", apply, true);
      mo.disconnect();
      document.documentElement.style.removeProperty("--ai-fab-clearance");
    };
  }, [enabled]);
};

export default useAiFloatClearance;
